import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

export const MENU_ID_SHOW = 'sonar-table/ContextMenuShow';
export const MENU_ID_HIDE = 'sonar-table/ContextMenuHide';

import { SORT_ORDER } from '../../constants';
import { selectIsAllSelected, selectSonarTableEntries } from '../../redux/sonar/sonarSelectors';
import sortDomains from '../../utils/sortDomains';
import trendEvaluator from '../../utils/trendEvaluator';
import ThCheckbox from '../../components/common-table/components/ThCheckbox';
import Tr from '../../components/common-table/components/Tr';
import ThWithSort from '../../components/common-table/components/ThWithSort';
import Th from '../../components/common-table/components/Th';
import SonarTableActions from './SonarTableActions';
import SONAR_TABLE_HEADER from './constant';
import SonarTableContextMenu from './SonarTableContextMenu';
import SonarTableItem from './SonarTableItem';
import VirtualizedTable from '../../components/common-table/VirtualizedTable';

import scss from './SonarTable.module.scss';
import { chooseDomain, selectAllDomains } from '../../redux/sonar/sonarReducer';

const SonarTable = ({
  columnsToShow,
  classes = {
    wrapper: '',
  },
  isDedicated,
  initialSort = {
    key: 'powerrank',
    order: SORT_ORDER.DESC,
  },
  rankLabel,
  rankKey,
  disableGlobalRank,
}) => {
  const { t } = useTranslation('sonar');
  const dispatch = useDispatch();
  const [sort, setSort] = useState(initialSort);

  const columnSet = useMemo(() => columnsToShow && new Set(columnsToShow), [columnsToShow]);

  const isShowDedicated = useSelector((state) => state.sonarDashboard.isShowDedicated);
  const searchQuery = useSelector((state) => state.sonarDashboard.sonarTable.searchQuery);
  const isShowGlobalRank = useSelector((state) => state.sonarDashboard.isShowGlobalRank);
  const sonarTableEntries = useSelector(selectSonarTableEntries);
  const isAllSelected = useSelector(selectIsAllSelected);

  const handleSelectAll = () => {
    dispatch(selectAllDomains());
  };

  const handleChooseDomain = (domain) => {
    dispatch(chooseDomain(domain));
  };

  const handleSort = (columnKey) => {
    setSort((prevSort) => {
      const isActive = columnKey === prevSort.key;
      const isAsc = prevSort.order === SORT_ORDER.ASC;

      return {
        key: columnKey,
        order: isActive ? (isAsc ? SORT_ORDER.DESC : SORT_ORDER.ASC) : SORT_ORDER.ASC,
      };
    });
  };

  const finalColumns = useMemo(
    () => (!columnSet ? SONAR_TABLE_HEADER : SONAR_TABLE_HEADER.filter(({ key }) => columnSet.has(key))),
    [columnSet],
  );

  if (isDedicated && !isShowDedicated) {
    return null;
  }

  const rows = sortDomains(sonarTableEntries, sort);

  return (
    <div className={clsx(scss.scrollable, classes.wrapper)}>
      <SonarTableActions isDedicated={isDedicated} disableGlobalRank={disableGlobalRank} />

      <VirtualizedTable
        content={rows}
        isLoading={false}
        renderBody={(item, index) => (
          <SonarTableItem
            key={`TableRow-${item.domain}-${index}`}
            data={item}
            trendEvaluator={trendEvaluator}
            onChooseDomain={handleChooseDomain}
            searchQuery={searchQuery}
            columns={finalColumns}
            rankKey={rankKey}
            isShowGlobalRank={isShowGlobalRank}
          />
        )}>
        <Tr extraClasses={scss.headerPadding}>
          <ThCheckbox selected={isAllSelected} onSelect={handleSelectAll} />
          <ThWithSort
            label={isDedicated && isShowGlobalRank ? t('rankGlobal') : rankLabel || t('rank')}
            sort={sort}
            onSort={handleSort}
            columnKey={`${isShowGlobalRank ? 'powerrank' : rankKey || 'powerrank'}_rank`}
            extraThClass={clsx(scss.cellRank, scss.cellRankHeader)}
          />
          <Th extraClasses='cell-title' isLeft>
            {t('company')}
          </Th>
          {finalColumns.map(({ key, title }) => (
            <ThWithSort
              key={key}
              label={t(title)}
              sort={sort}
              onSort={handleSort}
              columnKey={key}
              extraThClass='cell-value'
            />
          ))}
        </Tr>
      </VirtualizedTable>

      <SonarTableContextMenu id={MENU_ID_HIDE} hideLabel={t('hide')} />
      <SonarTableContextMenu id={MENU_ID_SHOW} hideLabel={t('show')} />
    </div>
  );
};

export default SonarTable;
